import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Calendar, Clock, ArrowLeft, BookOpen } from 'lucide-react';
import PublicHeader from '../components/layout/PublicHeader';
import type { BlogPost } from '../types';
import { SEO_BASE_URL } from '../constants/seo';
import { blogPosts, BLOG_POSTS_COUNT, HIDDEN_BLOG_POST_IDS, getBlogPostBySlugOrId, getBlogPostUrlSlug } from '../data/blogPosts';
import { pillarPages, getPillarBySlug } from '../data/pillarPages';
import './BlogPage.css';

const BlogPage: React.FC = () => {
  const navigate = useNavigate();
  const { postId } = useParams<{ postId?: string }>();

  const selectedPost = postId ? getBlogPostBySlugOrId(postId) : null;
  const selectedPillar = postId ? getPillarBySlug(postId) : null;

  const filteredPosts = [...blogPosts]
    .filter((p) => !HIDDEN_BLOG_POST_IDS.has(p.id))
    .sort((a, b) => new Date(b.publishedDate).getTime() - new Date(a.publishedDate).getTime());

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, []);

  // Redirect numeric IDs to canonical slug URLs (SEO: human-readable URLs)
  // Skip redirect for pillar pages
  useEffect(() => {
    if (postId && selectedPost && !selectedPillar) {
      const canonicalSlug = getBlogPostUrlSlug(selectedPost);
      if (postId !== canonicalSlug) {
        navigate(`/blog/${canonicalSlug}`, { replace: true });
      }
    }
  }, [postId, selectedPost, selectedPillar, navigate]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  // Generate Article Schema for SEO
  const generateArticleSchema = (post: BlogPost) => {
    const baseUrl = SEO_BASE_URL;
    const slug = getBlogPostUrlSlug(post);
    const articleUrl = `${baseUrl}/blog/${slug}`;

    // Format date to ISO 8601 format (YYYY-MM-DD)
    const publishedDate = post.publishedDate;

    const schema: any = {
      "@context": "https://schema.org",
      "@type": "BlogPosting", // Using BlogPosting instead of Article for blog posts
      "headline": post.title,
      "url": articleUrl,
      "description": post.metaDescription ?? post.excerpt,
      "author": {
        "@type": "Person",
        "name": post.author
      },
      "publisher": {
        "@type": "Organization",
        "name": "Focuzon",
        "logo": {
          "@type": "ImageObject",
          "url": `${baseUrl}/focuzon.png`
        }
      },
      "datePublished": publishedDate,
      "dateModified": publishedDate,
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": articleUrl
      }
    };

    // Add image if available (as array for better SEO)
    if (post.image) {
      schema.image = [
        {
          "@type": "ImageObject",
          "url": post.image.startsWith('http') ? post.image : `${baseUrl}${post.image}`
        }
      ];
    }

    return schema;
  };

  // Generate FAQ Schema (only include if there are actual FAQs)
  const generateFAQSchema = (post: BlogPost) => {
    if (post.faqs && post.faqs.length > 0) {
      return {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": post.faqs.map(faq => ({
          "@type": "Question",
          "name": faq.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": faq.answer
          }
        }))
      };
    }
    return null;
  };

  // Generate Breadcrumb Schema for SEO
  const generateBreadcrumbSchema = (post: BlogPost) => {
    const baseUrl = SEO_BASE_URL;
    const articleUrl = `${baseUrl}/blog/${getBlogPostUrlSlug(post)}`;

    return {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": `${baseUrl}/`
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Blog",
          "item": `${baseUrl}/blog`
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": post.title,
          "item": articleUrl
        }
      ]
    };
  };

  // Pillar page: topic authority hub linking related posts
  if (selectedPillar) {
    const pillarPosts = selectedPillar.postSlugs
      .map((slug) => getBlogPostBySlugOrId(slug))
      .filter((p): p is BlogPost => !!p);

    const pillarFaqSchema = selectedPillar.faqs.length > 0
      ? {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": selectedPillar.faqs.map((faq) => ({
            "@type": "Question",
            "name": faq.question,
            "acceptedAnswer": { "@type": "Answer", "text": faq.answer },
          })),
        }
      : null;

    const pillarBreadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": `${SEO_BASE_URL}/` },
        { "@type": "ListItem", "position": 2, "name": "Blog", "item": `${SEO_BASE_URL}/blog` },
        {
          "@type": "ListItem",
          "position": 3,
          "name": selectedPillar.title,
          "item": `${SEO_BASE_URL}/blog/${selectedPillar.slug}`,
        },
      ],
    };

    return (
      <>
        <Helmet>
          <title>{selectedPillar.metaTitle}</title>
          <meta name="description" content={selectedPillar.metaDescription} />
          <link rel="canonical" href={`${SEO_BASE_URL}/blog/${selectedPillar.slug}`} />
          <meta property="og:title" content={selectedPillar.metaTitle} />
          <meta property="og:description" content={selectedPillar.metaDescription} />
          <meta property="og:url" content={`${SEO_BASE_URL}/blog/${selectedPillar.slug}`} />
          <meta property="og:type" content="website" />
          <meta property="og:image" content={`${SEO_BASE_URL}/og-image.jpg`} />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={selectedPillar.metaTitle} />
          <meta name="twitter:description" content={selectedPillar.metaDescription} />
          {pillarFaqSchema && (
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(pillarFaqSchema) }} />
          )}
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(pillarBreadcrumbSchema) }} />
        </Helmet>
        <div className="min-h-screen bg-[#FFF8F0] relative overflow-x-hidden">
          <div className="fixed inset-0 pointer-events-none z-0">
            <div className="absolute top-0 -left-1/4 w-96 h-96 bg-gradient-to-br from-orange-300/50 to-orange-200/40 rounded-full blur-3xl"></div>
            <div className="absolute top-1/4 -right-1/4 w-96 h-96 bg-gradient-to-br from-orange-400/50 to-orange-300/40 rounded-full blur-3xl"></div>
          </div>

          <PublicHeader />

          <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-12 relative z-10">
            <button
              onClick={() => navigate('/blog')}
              className="flex items-center gap-2 text-gray-600 hover:text-orange-600 mb-6 transition-colors group"
            >
              <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
              <span className="font-medium">Back to Blog</span>
            </button>

            <article className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 md:p-12 border border-orange-200/50 shadow-lg">
              <div className="mb-6">
                <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm font-medium">
                  Pillar Guide
                </span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-8">
                {selectedPillar.title}
              </h1>

              <div
                className="blog-content text-gray-700 mb-10"
                dangerouslySetInnerHTML={{ __html: selectedPillar.intro }}
              />

              <h2 className="text-2xl font-bold text-gray-900 mb-6">All Articles in This Guide</h2>
              <div className="grid md:grid-cols-2 gap-6">
                {pillarPosts.map((post) => (
                  <div
                    key={post.id}
                    onClick={() => navigate(`/blog/${getBlogPostUrlSlug(post)}`)}
                    className="bg-gray-50/80 rounded-xl p-6 border border-orange-200/50 hover:shadow-lg cursor-pointer transition-all hover:border-orange-300"
                  >
                    <span className="text-sm text-orange-600 font-medium">{post.category}</span>
                    <h3 className="text-xl font-semibold text-gray-900 mt-2 mb-2">{post.title}</h3>
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">{post.excerpt}</p>
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span>{formatDate(post.publishedDate)}</span>
                      <span>•</span>
                      <span>{post.readTime} min read</span>
                    </div>
                  </div>
                ))}
              </div>

              {selectedPillar.faqs.length > 0 && (
                <div className="mt-12 pt-8 border-t border-orange-200/50">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h2>
                  <div className="space-y-6">
                    {selectedPillar.faqs.map((faq, i) => (
                      <div key={i}>
                        <h3 className="font-semibold text-gray-900 mb-2">{faq.question}</h3>
                        <p className="text-gray-600">{faq.answer}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </article>

            {/* Other pillar guides */}
            <div className="mt-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">More Topic Guides</h2>
              <div className="grid md:grid-cols-2 gap-6">
                {pillarPages
                  .filter((p) => p.slug !== selectedPillar.slug)
                  .map((pillar) => (
                    <div
                      key={pillar.slug}
                      onClick={() => navigate(`/blog/${pillar.slug}`)}
                      className="flex items-start gap-4 bg-white/80 backdrop-blur-sm rounded-xl p-6 border border-orange-200/50 hover:shadow-lg cursor-pointer transition-all"
                    >
                      <BookOpen className="w-8 h-8 text-orange-500 flex-shrink-0 mt-1" />
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">{pillar.title}</h3>
                        <p className="text-gray-600 text-sm">{pillar.postSlugs.length} articles</p>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </main>
        </div>
      </>
    );
  }

  if (selectedPost) {
    const articleSchema = generateArticleSchema(selectedPost);
    const faqSchema = generateFAQSchema(selectedPost);
    const breadcrumbSchema = generateBreadcrumbSchema(selectedPost);
    const postSlug = getBlogPostUrlSlug(selectedPost);
    const parentPillar = pillarPages.find((p) => p.postSlugs.includes(postSlug));

    return (
      <>
        <Helmet>
          <title>{selectedPost.metaTitle ?? `${selectedPost.title} | Focuzon Blog`}</title>
          <meta name="description" content={selectedPost.metaDescription ?? selectedPost.excerpt} />
          <link rel="canonical" href={`${SEO_BASE_URL}/blog/${getBlogPostUrlSlug(selectedPost)}`} />
          <meta property="og:title" content={selectedPost.metaTitle ?? selectedPost.title} />
          <meta property="og:description" content={selectedPost.metaDescription ?? selectedPost.excerpt} />
          <meta property="og:url" content={`${SEO_BASE_URL}/blog/${getBlogPostUrlSlug(selectedPost)}`} />
          <meta property="og:type" content="article" />
          <meta property="og:image" content={selectedPost.image?.startsWith('http') ? selectedPost.image : `${SEO_BASE_URL}${selectedPost.image ?? '/og-image.jpg'}`} />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={selectedPost.metaTitle ?? selectedPost.title} />
          <meta name="twitter:description" content={selectedPost.metaDescription ?? selectedPost.excerpt} />
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
          {faqSchema && (
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
          )}
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
        </Helmet>
        <div className="min-h-screen bg-[#FFF8F0] relative overflow-x-hidden">
          {/* Background gradient shapes */}
          <div className="fixed inset-0 pointer-events-none z-0">
            <div className="absolute top-0 -left-1/4 w-96 h-96 bg-gradient-to-br from-orange-300/50 to-orange-200/40 rounded-full blur-3xl"></div>
            <div className="absolute top-1/4 -right-1/4 w-96 h-96 bg-gradient-to-br from-orange-400/50 to-orange-300/40 rounded-full blur-3xl"></div>
          </div>

          <PublicHeader />

          {/* Blog Post Content - Add padding-top for fixed header */}
          <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-12 relative z-10">
            {/* Back Button */}
            <button
              onClick={() => navigate('/blog')}
              className="flex items-center gap-2 text-gray-600 hover:text-orange-600 mb-6 transition-colors group"
            >
              <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
              <span className="font-medium">Back to Blog</span>
            </button>

            <article className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 md:p-12 border border-orange-200/50 shadow-lg">
              {/* Header */}
              <div className="mb-8">
                <div className="flex items-center gap-4 mb-4">
                  <div className="flex gap-2 flex-wrap">
                    <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-sm whitespace-nowrap">
                      {selectedPost.category}
                    </span>
                    {selectedPost.tags.map(tag => (
                      <span
                        key={tag}
                        className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-sm whitespace-nowrap"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
                <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                  {selectedPost.title}
                </h1>
                <div className="flex flex-wrap items-center gap-6 text-gray-600">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-5 h-5" />
                    <span>{formatDate(selectedPost.publishedDate)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-5 h-5" />
                    <span>{selectedPost.readTime} min read</span>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div
                className="blog-content text-gray-700"
                dangerouslySetInnerHTML={{ __html: selectedPost.content }}
              />
            </article>

            {/* Related Posts */}
            <div className="mt-12">
              {parentPillar && (
                <div className="mb-6 p-4 bg-orange-50/80 rounded-xl border border-orange-200/50">
                  <p className="text-gray-700">
                    This article is part of our{' '}
                    <a
                      href={`/blog/${parentPillar.slug}`}
                      onClick={(e) => { e.preventDefault(); navigate(`/blog/${parentPillar.slug}`); }}
                      className="font-semibold text-orange-600 hover:text-orange-700 underline"
                    >
                      {parentPillar.title}
                    </a>
                    {' '}—explore all {parentPillar.postSlugs.length} articles in that guide.
                  </p>
                </div>
              )}
              <h2 className="text-2xl font-bold text-gray-900 mb-6">More Articles</h2>
              <div className="grid md:grid-cols-2 gap-6">
                {blogPosts
                  .filter(post => post.id !== selectedPost.id && !HIDDEN_BLOG_POST_IDS.has(post.id))
                  .slice(0, 2)
                  .map(post => (
                    <div
                      key={post.id}
                      onClick={() => navigate(`/blog/${getBlogPostUrlSlug(post)}`)}
                      className="bg-white/80 backdrop-blur-sm rounded-xl p-6 border border-orange-200/50 hover:shadow-lg cursor-pointer transition-all"
                    >
                      <span className="text-sm text-orange-600 font-medium">{post.category}</span>
                      <h3 className="text-xl font-semibold text-gray-900 mt-2 mb-2">{post.title}</h3>
                      <p className="text-gray-600 text-sm mb-4">{post.excerpt}</p>
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span>{formatDate(post.publishedDate)}</span>
                        <span>•</span>
                        <span>{post.readTime} min read</span>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </main>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Blog | Focuzon - Productivity &amp; Focus Insights</title>
        <meta name="description" content="Discover insights on productivity, focus, deep work, and mindfulness from the Focuzon team." />
        <link rel="canonical" href={`${SEO_BASE_URL}/blog`} />
        <meta property="og:title" content="Blog | Focuzon - Productivity &amp; Focus Insights" />
        <meta property="og:description" content="Discover insights on productivity, focus, deep work, and mindfulness from the Focuzon team." />
        <meta property="og:url" content={`${SEO_BASE_URL}/blog`} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={`${SEO_BASE_URL}/og-image.jpg`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Blog | Focuzon - Productivity &amp; Focus Insights" />
        <meta name="twitter:description" content="Discover insights on productivity, focus, deep work, and mindfulness from the Focuzon team." />
      </Helmet>
      <div className="min-h-screen bg-[#FFF8F0] relative overflow-x-hidden">
        {/* Background gradient shapes */}
        <div className="fixed inset-0 pointer-events-none z-0">
          <div className="absolute top-0 -left-1/4 w-96 h-96 bg-gradient-to-br from-orange-300/50 to-orange-200/40 rounded-full blur-3xl"></div>
          <div className="absolute top-1/4 -right-1/4 w-96 h-96 bg-gradient-to-br from-orange-400/50 to-orange-300/40 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-1/4 w-80 h-80 bg-gradient-to-br from-orange-200/40 to-orange-100/30 rounded-full blur-3xl"></div>
        </div>

        <PublicHeader />

        {/* Blog Content - Add padding-top for fixed header */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16 relative z-10">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Focuzon Blog
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Insights on productivity, focus, deep work, and building better work habits.
            </p>
            <p className="text-sm text-gray-500 mt-2">
              Showing all {BLOG_POSTS_COUNT} articles
            </p>
          </div>

          {/* Pillar / Topic Guides */}
          <div className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Topic Guides</h2>
            <p className="text-gray-600 mb-6 max-w-2xl">
              Comprehensive guides that link all our best articles on each topic. Start here for deep dives.
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              {pillarPages.map((pillar) => (
                <div
                  key={pillar.slug}
                  onClick={() => navigate(`/blog/${pillar.slug}`)}
                  className="flex flex-col bg-white/90 backdrop-blur-sm rounded-2xl p-6 border-2 border-orange-200/60 hover:border-orange-300 hover:shadow-xl cursor-pointer transition-all hover:-translate-y-1"
                >
                  <BookOpen className="w-10 h-10 text-orange-500 mb-4" />
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{pillar.title}</h3>
                  <p className="text-gray-600 text-sm flex-1 mb-4">{pillar.postSlugs.length} articles</p>
                  <span className="text-orange-600 font-medium text-sm">Explore guide →</span>
                </div>
              ))}
            </div>
          </div>

          {/* Blog Posts Grid - all posts visible, equal card heights */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {filteredPosts.map(post => (
              <div
                key={post.id}
                onClick={() => navigate(`/blog/${getBlogPostUrlSlug(post)}`)}
                className="flex flex-col bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-orange-200/50 hover:shadow-xl cursor-pointer transition-all hover:-translate-y-1 min-h-[280px]"
              >
                <div className="flex items-center gap-2 mb-4">
                  <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-medium">
                    {post.category}
                  </span>
                </div>
                <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3 line-clamp-2 flex-shrink-0">
                  {post.title}
                </h2>
                <p className="text-gray-600 mb-4 line-clamp-3 flex-1 min-h-0">
                  {post.excerpt}
                </p>
                <div className="flex items-center justify-between text-sm text-gray-500 pt-4 border-t border-orange-100 flex-shrink-0">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 flex-shrink-0" />
                    <span>{formatDate(post.publishedDate)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 flex-shrink-0" />
                    <span>{post.readTime} min</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Empty State */}
          {filteredPosts.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-600 text-lg">No posts found in this category.</p>
            </div>
          )}
        </main>
      </div>
    </>
  );
};

export default BlogPage;
